import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Receipt, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/axiosConfig';

const CURRENCY_SYMBOLS = { NOK: 'kr', USD: '$', EUR: '€' };

function formatAmount(cents, currency) {
  const sym = CURRENCY_SYMBOLS[currency?.toUpperCase()] || currency || '';
  const amount = cents / 100;
  return sym === '$' || sym === '€' ? `${sym}${amount.toFixed(2)}` : `${amount.toFixed(0)} ${sym}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

const STATUS_LABELS = {
  success: 'Success',
  failed: 'Failed',
  refunded: 'Refunded',
  pending: 'Pending',
  canceled: 'Canceled',
};

export default function BuyHistoryModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api
      .get('/api/stripe/orders')
      .then((res) => setOrders(res.data?.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className='fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200'
      style={{ zIndex: 10001 }}
      onClick={onClose}
    >
      <div
        className='bg-card border border-border rounded-xl shadow-2xl flex flex-col max-w-[90vw] w-[560px] max-h-[90vh]'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between px-5 py-4 border-b border-border shrink-0'>
          <h2 className='text-base font-semibold text-foreground flex items-center gap-2'>
            <Receipt className='h-5 w-5' />
            {t('settings.buyHistory')}
          </h2>
          <Button variant='ghost' size='icon' className='h-8 w-8' onClick={onClose}>
            <X className='h-4 w-4' />
          </Button>
        </div>

        <div className='p-5 overflow-y-auto min-h-0 flex-1'>
          {loading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          ) : orders.length === 0 ? (
            <p className='text-sm text-muted-foreground text-center py-8'>
              {t('settings.buyHistoryEmpty')}
            </p>
          ) : (
            <div className='space-y-3'>
              {orders.map((order) => (
                <div
                  key={order.id}
                  className='p-3 rounded-lg border border-border bg-muted/30'
                >
                  <div className='flex items-start justify-between gap-2'>
                    <div>
                      <p className='font-medium text-sm'>
                        {order.product_name || order.license_name || t('settings.unknownProduct')}
                      </p>
                      <p className='text-xs text-muted-foreground mt-0.5'>
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className='text-right shrink-0'>
                      <p className='font-medium text-sm'>
                        {formatAmount(order.amount_cents, order.currency)}
                      </p>
                      <span
                        className={`text-xs ${
                          order.status === 'success'
                            ? 'text-green-600 dark:text-green-400'
                            : order.status === 'failed'
                              ? 'text-destructive'
                              : 'text-muted-foreground'
                        }`}
                      >
                        {t(`settings.orderStatus.${order.status}`) || STATUS_LABELS[order.status] || order.status}
                      </span>
                    </div>
                  </div>
                  {order.stripe_fee_cents > 0 && (
                    <p className='text-xs text-muted-foreground mt-2'>
                      {t('settings.stripeFee')}: {formatAmount(order.stripe_fee_cents, order.currency)}
                      {order.stripe_fee_percent != null && ` (${order.stripe_fee_percent.toFixed(2)}%)`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
